<template>
    <AppLayout>
        <div v-if="user" class="mt-20 px-4 w-full">
            <div class="flex justify-center">
                <div class="w-full md:w-2/3 lg:w-1/2 xl:w-2/3 max-w-(--breakpoint-md)">
                    <form
                        v-focustrap
                        class="pt-5 text-center"
                        @submit.prevent="search"
                    >
                        <div class="mb-4">
                            <InputText
                                v-model="query"
                                class="w-full px-4 py-3 text-center text-lg border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                type="text"
                                name="query"
                                :placeholder="
                                    $t(`messages.searchPage.placeholder`)
                                "
                                autofocus
                            />
                        </div>

                        <div class="flex justify-center mt-3">
                            <div class="w-full md:w-1/3">
                                <Button
                                    class="w-full py-3 px-4 bg-blue-600 text-white text-lg font-medium rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                    type="submit"
                                    :disabled="!query"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        class="h-5 w-5 mr-2"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                        />
                                    </svg>
                                    <span>{{ $t("messages.search") }}</span>
                                </Button>
                            </div>
                        </div>

                        <div class="mt-4 flex justify-center">
                            <button
                                class="p-3 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-hidden focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                type="button"
                                :disabled="!query"
                                @click.stop.prevent="reset"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="h-6 w-6"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>

                    </form>
                </div>
            </div>

            <div v-if="evaluations?.finalized?.length > 0 || evaluations?.in_progress?.length > 0" class="my-10">

                <div class="w-full md:w-2/3 lg:w-1/2 xl:w-2/3 max-w-(--breakpoint-md) flex justify-center mx-auto">
                    <h2 class="text-2xl font-semibold text-gray-700 mb-4">Sinu hinnangud</h2>
                </div>

                <div class="flex gap-4 justify-center">
                    <div v-if="evaluations?.finalized?.length > 0" class="w-full md:w-2/3 lg:w-1/2 xl:w-2/3 max-w-(--breakpoint-md)">
                        <h2 class="text-xl font-bold text-gray-700 mb-4">Kinnitatud</h2>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <EvaluationCard v-for="evaluation in evaluations.finalized" :key="evaluation.id" :evaluation="evaluation" />
                        </div>
                    </div>

                    <div v-if="evaluations?.in_progress?.length > 0" class="w-full md:w-2/3 lg:w-1/2 xl:w-2/3 max-w-(--breakpoint-md)">
                        <div class="mb-4 flex items-center justify-between gap-2">
                            <h2 class="text-xl font-bold text-gray-700">
                                Hindamisel
                            </h2>

                            <div class="text-sm text-gray-600 flex items-center gap-2">
                                <Select
                                    v-model="selectedT1"
                                    :options="t1Options"
                                    size="small"
                                    class="border-none shadow-none"
                                    :class="{ 'bg-primary-50': selectedT1 }"
                                    :label-class="{ 'font-semibold text-primary-600': selectedT1 }"
                                    placeholder="Maakond"
                                    @change="handleFilterChange"
                                />

                                <Select
                                    v-model="selectedT2"
                                    :options="t2Options"
                                    size="small"
                                    class="border-none shadow-none"
                                    :class="{ 'bg-primary-50': selectedT2 }"
                                    :label-class="{ 'font-semibold text-primary-600': selectedT2 }"
                                    placeholder="Linn/vald"
                                    :disabled="!selectedT1"
                                    :dt="{
                                        disabled: {
                                            background: '#fff',
                                            color: '#c0c5cf',
                                        }
                                    }"
                                    @change="handleFilterChange"
                                />
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <EvaluationCard v-for="evaluation in filteredInProgress" :key="evaluation.id" :evaluation="evaluation" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script setup>
import { Link, router, usePage } from '@inertiajs/vue3'
import { ref, computed, watch, shallowRef } from 'vue'
import EvaluationCard from '@/components/evaluations/EvaluationCard.vue'
import AppLayout from '../Layouts/AppLayout.vue'

const props = defineProps({
  evaluations: {
    type: Object,
    default: () => ({
      finalized: [],
      in_progress: [],
    }),
  },
})

const page = usePage()
const user = computed(() => page.props.auth?.user)
const can = computed(() => page.props.auth?.can)

const query = ref('')

const selectedT1 = shallowRef('')
const selectedT2 = shallowRef('')

const reset = () => {
  query.value = ''
}

const search = () => {
  if (!query.value?.length) {
    return
  }
  router.get('/search?query=' + query.value)
}

const allEvaluations = computed(() => [
  ...(props.evaluations?.in_progress || []),
])

const uniqueSorted = arr => Array.from(new Set(arr)).filter(Boolean).sort((a, b) => a.localeCompare(b, 'et'))

const t1Options = computed(() => ['Kõik maakonnad', ...uniqueSorted(allEvaluations.value
  .map(e => e?.address?.tase1_nimetus)
  .filter(Boolean))])

const t2Options = computed(() => {
  if (!selectedT1.value) {
    return []
  }
  return ['Kõik', ...uniqueSorted(allEvaluations.value
    .filter(e => e?.address?.tase1_nimetus === selectedT1.value)
    .map(e => e?.address?.tase2_nimetus)
    .filter(Boolean))]
})

watch(selectedT1, () => {
  selectedT2.value = ''
})

const matchesFilters = e => {
  const t1 = e?.address?.tase1_nimetus
  const t2 = e?.address?.tase2_nimetus
  if (selectedT1.value && t1 !== selectedT1.value) return false
  if (selectedT2.value && t2 !== selectedT2.value) return false
  return true
}

const filteredInProgress = computed(() => (props.evaluations?.in_progress || []).filter(matchesFilters))

const handleFilterChange = (event) => {
  if (event.value === 'Kõik maakonnad') {
    selectedT1.value = ''
  }
  if (event.value === 'Kõik') {
    selectedT2.value = ''
  }
}

</script>
